import { all, call, put, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import errorHandler from "common/errorHandler";
import * as actions from "./actions";

// No tenantid header — the tenant is determined server-side from the auth-token claims.

const schemaPath = (businessAreaId, schemaId) => `${Endpoints.duplicates}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}`;

export function* getReviewQueueItemsRequest(api, action) {
    try {
        const { businessAreaId, schemaId, filters, pageKey, append } = action.params;
        const params = {};
        for (const key of ["status", "type", "definitionId"]) {
            if (filters && filters[key]) {
                params[key] = filters[key];
            }
        }
        if (pageKey) {
            params.pageKey = pageKey;
        }
        const result = yield call([api, api.get], `${schemaPath(businessAreaId, schemaId)}/review`, {}, { params });
        yield put(actions.getReviewQueueItemsSuccess(result.data, append));
    } catch (error) {
        yield put(actions.getReviewQueueItemsFailure(errorHandler(error, "Get", "review items")));
    }
}

export function* getReviewQueueCountsRequest(api, action) {
    try {
        const { businessAreaId, schemaId } = action.params;
        const result = yield call([api, api.get], `${schemaPath(businessAreaId, schemaId)}/review/counts`);
        yield put(actions.getReviewQueueCountsSuccess(result.data, schemaId));
    } catch (error) {
        yield put(actions.getReviewQueueCountsFailure(errorHandler(error, "Get", "review counts")));
    }
}

// A member's record as the data object GET returns it (PII masked); null when it no longer exists,
// { readError } when it could not be read, so one failure does not hide the other members.
export function* getReviewMemberRecord(api, schemaId, member) {
    try {
        const result = yield call(
            [api, api.get],
            `${Endpoints.datasets}/api/v1/businessareas/${member.businessAreaId}/schemas/${schemaId}/datasets/${member.dataSetId}/dataobjects/${member.dataObjectId}`,
        );
        return result.data || null;
    } catch (error) {
        if (error && error.response && error.response.status === 404) {
            return null;
        }
        return { readError: errorHandler(error, "Get", "record").message || "The record could not be read" };
    }
}

export function* getReviewQueueItemRequest(api, action) {
    try {
        const { businessAreaId, schemaId, itemId } = action.params;
        const result = yield call([api, api.get], `${schemaPath(businessAreaId, schemaId)}/review/${itemId}`);
        const item = result.data;
        // Members outside the caller's business area come back redacted; they are not read.
        const members = ((item && item.members) || []).filter((member) => !member.redacted && member.dataObjectId);
        const records = yield all(members.map((member) => call(getReviewMemberRecord, api, schemaId, member)));
        const recordsById = {};
        members.forEach((member, index) => {
            recordsById[member.dataObjectId] = records[index];
        });
        yield put(actions.getReviewQueueItemSuccess(item, recordsById));
    } catch (error) {
        yield put(actions.getReviewQueueItemFailure(errorHandler(error, "Get", "review item")));
    }
}

export function* getSchemaJobLockRequest(api, action) {
    try {
        const { businessAreaId, schemaId } = action.params;
        const result = yield call([api, api.get], `${schemaPath(businessAreaId, schemaId)}/locks`);
        yield put(actions.getSchemaJobLockSuccess(result.data));
    } catch (error) {
        yield put(actions.getSchemaJobLockFailure(errorHandler(error, "Get", "schema lock")));
    }
}

export function* breakSchemaJobLockRequest(api, action) {
    const { businessAreaId, schemaId, jobId } = action.params;
    try {
        const result = yield call([api, api.post], `${schemaPath(businessAreaId, schemaId)}/locks/break`, jobId ? { jobId } : {});
        yield put(actions.breakSchemaJobLockSuccess(result.data));
    } catch (error) {
        const failure = errorHandler(error, "Break", "schema lock");
        // 404: the job no longer holds the lock (it finished, or another job took it).
        if (error && error.response && error.response.status === 404) {
            failure.message = "This job no longer holds the lock.";
        }
        yield put(actions.breakSchemaJobLockFailure(failure));
    }
    // Either way, show the lock as it is now: broken, released, or someone else's.
    yield put(actions.getSchemaJobLockRequest(businessAreaId, schemaId));
}

// How a failed action is named to the steward.
const ACTION_LABELS = { merge: "Merge", dismiss: "Dismiss", birth: "Create golden record from", subsume: "Flag as subsumed" };

// A steward's merge or dismiss. Afterwards the queue shows what changed: the counts and the list
// on success; on a conflict (409), the item as it is now, for the steward to decide again.
export function* reviewItemActionRequest(api, action) {
    const { businessAreaId, schemaId, itemId, action: reviewAction, request } = action.params;
    try {
        const result = yield call([api, api.post], `${schemaPath(businessAreaId, schemaId)}/review/${itemId}/${reviewAction}`, request);
        yield put(actions.reviewItemActionSuccess(reviewAction, result.data));
        yield put(actions.getReviewQueueCountsRequest(businessAreaId, schemaId));
    } catch (error) {
        const conflict = !!(error && error.response && error.response.status === 409);
        const failure = errorHandler(error, ACTION_LABELS[reviewAction] || "Update", "review item");
        // The service explains a conflict in words a steward can act on.
        if (conflict && error.response.data && error.response.data.message) {
            failure.message = error.response.data.message;
        }
        yield put(actions.reviewItemActionFailure(reviewAction, failure, conflict));
        if (conflict) {
            yield put(actions.getReviewQueueItemRequest(businessAreaId, schemaId, itemId));
        }
    }
}

export function* getSubsumedRecordsRequest(api, action) {
    try {
        const { businessAreaId, schemaId, filters, pageKey, append } = action.params;
        const params = {};
        if (filters && filters.status) {
            params.status = filters.status;
        }
        if (pageKey) {
            params.pageKey = pageKey;
        }
        const result = yield call([api, api.get], `${schemaPath(businessAreaId, schemaId)}/subsumed`, {}, { params });
        yield put(actions.getSubsumedRecordsSuccess(result.data, append));
    } catch (error) {
        yield put(actions.getSubsumedRecordsFailure(errorHandler(error, "Get", "subsumed records")));
    }
}

// Retires flagged records; the answer says what became of each. The view reads its list again,
// with its own filter, once it has shown the outcome.
export function* retireSubsumedRequest(api, action) {
    const { businessAreaId, schemaId, request } = action.params;
    try {
        const result = yield call([api, api.post], `${schemaPath(businessAreaId, schemaId)}/subsumed/retire`, request);
        yield put(actions.retireSubsumedSuccess({ ...result.data, schemaId }));
    } catch (error) {
        const failure = errorHandler(error, "Retire", "subsumed records");
        // 409: a deduplication or duplicate search holds the schema; the service says so.
        if (error && error.response && error.response.status === 409 && error.response.data && error.response.data.message) {
            failure.message = error.response.data.message;
        }
        yield put(actions.retireSubsumedFailure({ ...failure, schemaId }));
    }
}

export function* restoreSubsumedRequest(api, action) {
    const { businessAreaId, schemaId, dataObjectId, request } = action.params;
    try {
        const result = yield call([api, api.post], `${schemaPath(businessAreaId, schemaId)}/subsumed/${dataObjectId}/restore`, request);
        yield put(actions.restoreSubsumedSuccess({ ...result.data, schemaId }));
    } catch (error) {
        const failure = errorHandler(error, "Restore", "record");
        // 409: its primary key was taken, it cannot be restored, or the schema is busy; the
        // service says which.
        if (error && error.response && error.response.status === 409 && error.response.data && error.response.data.message) {
            failure.message = error.response.data.message;
        }
        yield put(actions.restoreSubsumedFailure(dataObjectId, { ...failure, schemaId }));
    }
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_REVIEW_QUEUE_ITEMS_REQUEST, getReviewQueueItemsRequest, api);
    yield takeLatest(actions.GET_REVIEW_QUEUE_COUNTS_REQUEST, getReviewQueueCountsRequest, api);
    yield takeLatest(actions.GET_REVIEW_QUEUE_ITEM_REQUEST, getReviewQueueItemRequest, api);
    yield takeLatest(actions.GET_SCHEMA_JOB_LOCK_REQUEST, getSchemaJobLockRequest, api);
    yield takeLatest(actions.BREAK_SCHEMA_JOB_LOCK_REQUEST, breakSchemaJobLockRequest, api);
    yield takeLatest(actions.REVIEW_ITEM_ACTION_REQUEST, reviewItemActionRequest, api);
    yield takeLatest(actions.GET_SUBSUMED_RECORDS_REQUEST, getSubsumedRecordsRequest, api);
    yield takeLatest(actions.RETIRE_SUBSUMED_REQUEST, retireSubsumedRequest, api);
    yield takeLatest(actions.RESTORE_SUBSUMED_REQUEST, restoreSubsumedRequest, api);
}
